import { useState } from 'react';
import { Button, Input, Select, SelectItem, Spinner } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { useLogSearch } from '@/features/logs/hooks';
import { cn } from '@/lib/cn';
import type { LogQuery } from '@/features/logs/types';
import { MagnifyingGlassIcon, DocumentTextIcon, ArrowPathIcon } from '@/icons';

const levelClass = (lvl?: string) =>
  lvl === 'error' || lvl === 'warn' ? 'text-warning' : 'text-fg';

/** Mail log search — component/address/text filters over the real /logs endpoint. */
export function LogsPage() {
  const [draft, setDraft] = useState<LogQuery>({ component: 'all', limit: 200 });
  const [query, setQuery] = useState<LogQuery | null>(null);
  const { data, isFetching, isError, error, refetch } = useLogSearch(query ?? {}, !!query);

  const run = () => setQuery({ ...draft });

  return (
    <div>
      <PageHeader title="Logs" description="Search Postfix, Dovecot, Rspamd and webmail logs." />

      <div className="mb-4 rounded-xl border border-border bg-surface p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="Component" size="sm" selectedKeys={new Set([draft.component ?? 'all'])}
            onSelectionChange={(k) => setDraft((d) => ({ ...d, component: String(Array.from(k)[0] ?? 'all') }))}>
            <SelectItem key="all">All</SelectItem>
            <SelectItem key="postfix">Postfix</SelectItem>
            <SelectItem key="dovecot">Dovecot</SelectItem>
            <SelectItem key="rspamd">Rspamd</SelectItem>
            <SelectItem key="roundcube">Webmail</SelectItem>
          </Select>
          <Input label="Address" size="sm" placeholder="user@acme.com" value={draft.address ?? ''} onValueChange={(v) => setDraft((d) => ({ ...d, address: v }))} />
          <Input label="Contains" size="sm" placeholder="text to match" value={draft.search ?? ''} onValueChange={(v) => setDraft((d) => ({ ...d, search: v }))} />
          <div className="flex items-end">
            <Button color="primary" fullWidth startContent={<MagnifyingGlassIcon className="h-4 w-4" />} onPress={run} isLoading={isFetching}>Search</Button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface">
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <span className="text-xs text-fg-subtle">{data ? `${data.length} lines` : 'Run a search to view logs'}</span>
          {query && <Button isIconOnly size="sm" variant="light" aria-label="Refresh" onPress={() => refetch()}><ArrowPathIcon className={cn('h-4 w-4', isFetching && 'animate-spin')} /></Button>}
        </div>
        {!query ? (
          <EmptyState icon={DocumentTextIcon} title="Search the mail logs" description="Pick a component and optional filters, then run a search. Results are scoped to your tenant." />
        ) : isError && !data ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : isFetching && !data ? (
          <div className="flex justify-center py-16"><Spinner label="Searching…" /></div>
        ) : (data?.length ?? 0) === 0 ? (
          <EmptyState icon={DocumentTextIcon} title="No matching log lines" description="Try widening the time range or removing filters." />
        ) : (
          <div className="max-h-[60vh] overflow-auto p-2 font-mono text-xs">
            {(data ?? []).map((e, i) => (
              <div key={i} className="flex gap-3 border-b border-border/50 px-2 py-1 hover:bg-surface-raised/40">
                <span className="shrink-0 text-fg-subtle">{new Date(e.timestamp).toLocaleTimeString()}</span>
                <span className="w-16 shrink-0 text-brand">{e.component}</span>
                <span className={cn('flex-1 whitespace-pre-wrap break-all', levelClass(e.level))}>{e.line}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
